import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import XLSXStyle from 'xlsx-js-style';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import './SaleRegister.css';

function SaleRegister() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [searchOpen, setSearchOpen] = useState(false);

    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, token]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (!hasReadReportPermission) return;
        try {
            if (selectedOption === 'itemWise') {
                const response = await fetchData('ReportApi', '/SaleRegister/' + companyId + '/' + fromDate + '/' + toDate, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                    setDataBill([]);
                }
            } else {
                const response = await fetchData('ReportApi', '/SaleBillWise/' + companyId + '/' + fromDate + '/' + toDate, token);
                if (response.responseCode === '1111') {
                    setDataBill(response.response_data);
                    setDataItem([]);
                }
            }
        } catch (error) {
            console.error('Error fetching Sale Register Details:', error);
        }
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const filteredDataBill = dataBill.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleExportExcel = () => {
        const isItem = selectedOption === 'itemWise';
        const headers = isItem
            ? ['Sl.No', 'Item Name', 'Quantity', 'Rate', 'Amount']
            : ['Sl.No', 'Date', 'Bill Details', 'Cash Amount', 'Card Amount', 'UPI Amount'];
        const colCount = headers.length;

        // Style definitions
        const titleStyle = {
            font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '6D28D9' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: { bottom: { style: 'medium', color: { rgb: '4C1D95' } } },
        };
        const subTitleStyle = {
            font: { bold: true, sz: 11, color: { rgb: '4C1D95' } },
            fill: { fgColor: { rgb: 'EDE9FE' } },
            alignment: { horizontal: 'center', vertical: 'center' },
        };
        const headerStyle = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '7C3AED' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
                top: { style: 'thin', color: { rgb: '5B21B6' } },
                bottom: { style: 'medium', color: { rgb: '5B21B6' } },
                left: { style: 'thin', color: { rgb: '5B21B6' } },
                right: { style: 'thin', color: { rgb: '5B21B6' } },
            },
        };
        const cellStyleOdd = {
            font: { sz: 10, color: { rgb: '1F2937' } },
            fill: { fgColor: { rgb: 'FFFFFF' } },
            alignment: { vertical: 'center' },
            border: {
                top: { style: 'thin', color: { rgb: 'E5E7EB' } },
                bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
                left: { style: 'thin', color: { rgb: 'E5E7EB' } },
                right: { style: 'thin', color: { rgb: 'E5E7EB' } },
            },
        };
        const cellStyleEven = {
            ...cellStyleOdd,
            fill: { fgColor: { rgb: 'F5F3FF' } },
        };
        const totalStyle = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '10B981' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
                top: { style: 'medium', color: { rgb: '059669' } },
                bottom: { style: 'medium', color: { rgb: '059669' } },
                left: { style: 'thin', color: { rgb: '059669' } },
                right: { style: 'thin', color: { rgb: '059669' } },
            },
        };
        const numStyle = (isEven) => ({
            ...(isEven ? cellStyleEven : cellStyleOdd),
            alignment: { horizontal: 'right', vertical: 'center' },
        });
        const totalNumStyle = {
            ...totalStyle,
            alignment: { horizontal: 'right', vertical: 'center' },
        };

        const ws = {};
        const range = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };

        const setCell = (r, c, value, style) => {
            const addr = XLSXStyle.utils.encode_cell({ r, c });
            ws[addr] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
            if (r > range.e.r) range.e.r = r;
            if (c > range.e.c) range.e.c = c;
        };

        // Row 0: Title (merged)
        const titleText = (isItem ? 'Sale Register - Item Wise' : 'Sale Register - Bill Wise');
        for (let c = 0; c < colCount; c++) setCell(0, c, c === 0 ? titleText : '', titleStyle);

        // Row 1: Date range (merged)
        const dateText = 'From : ' + formatDate(fromDate) + '   To : ' + formatDate(toDate);
        for (let c = 0; c < colCount; c++) setCell(1, c, c === 0 ? dateText : '', subTitleStyle);

        // Row 2: Headers
        headers.forEach((h, c) => setCell(2, c, h, headerStyle));

        // Data rows
        const dataRows = isItem ? filteredDataItem : filteredDataBill;
        dataRows.forEach((row, i) => {
            const r = i + 3;
            const isEven = i % 2 === 1;
            const cs = (align) => align === 'right' ? numStyle(isEven) : (isEven ? cellStyleEven : cellStyleOdd);
            if (isItem) {
                setCell(r, 0, i + 1, cs());
                setCell(r, 1, row.itemname, cs());
                setCell(r, 2, parseFloat(row.qty), cs('right'));
                setCell(r, 3, parseFloat(row.salerate), cs('right'));
                setCell(r, 4, parseFloat(row.amount), cs('right'));
            } else {
                setCell(r, 0, i + 1, cs());
                setCell(r, 1, formatDate(row.date1), cs());
                setCell(r, 2, row.billno, cs());
                setCell(r, 3, parseFloat(row.cashamout), cs('right'));
                setCell(r, 4, parseFloat(row.cardamount), cs('right'));
                setCell(r, 5, parseFloat(row.upiamount), cs('right'));
            }
        });

        // Total row
        const totalRow = dataRows.length + 3;
        if (isItem) {
            setCell(totalRow, 0, '', totalStyle);
            setCell(totalRow, 1, 'TOTAL', totalStyle);
            setCell(totalRow, 2, parseFloat(filteredDataItem.reduce((s, r) => s + parseFloat(r.qty), 0).toFixed(2)), totalNumStyle);
            setCell(totalRow, 3, '', totalStyle);
            setCell(totalRow, 4, parseFloat(filteredDataItem.reduce((s, r) => s + parseFloat(r.amount), 0).toFixed(2)), totalNumStyle);
        } else {
            setCell(totalRow, 0, '', totalStyle);
            setCell(totalRow, 1, '', totalStyle);
            setCell(totalRow, 2, 'TOTAL', totalStyle);
            setCell(totalRow, 3, parseFloat(filteredDataBill.reduce((s, r) => s + parseFloat(r.cashamout), 0).toFixed(2)), totalNumStyle);
            setCell(totalRow, 4, parseFloat(filteredDataBill.reduce((s, r) => s + parseFloat(r.cardamount), 0).toFixed(2)), totalNumStyle);
            setCell(totalRow, 5, parseFloat(filteredDataBill.reduce((s, r) => s + parseFloat(r.upiamount), 0).toFixed(2)), totalNumStyle);
        }

        ws['!ref'] = XLSXStyle.utils.encode_range(range);

        // Merge title and subtitle rows
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
        ];

        // Column widths
        ws['!cols'] = isItem
            ? [{ wch: 8 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 14 }]
            : [{ wch: 8 }, { wch: 14 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];

        // Row heights
        ws['!rows'] = [{ hpt: 28 }, { hpt: 20 }, { hpt: 20 }];

        const wb = XLSXStyle.utils.book_new();
        XLSXStyle.utils.book_append_sheet(wb, ws, 'Sale Register');
        XLSXStyle.writeFile(wb, 'SaleRegister.xlsx');
    };

    const handlePrint = () => {
        const title = selectedOption === 'itemWise' ? 'Sale Register (Item Wise)' : 'Sale Register (Bill Wise)';
        const rows = selectedOption === 'itemWise'
            ? filteredDataItem.map((r, i) =>
                '<tr><td>' + (i + 1) + '</td><td>' + r.itemname + '</td><td>' + r.qty + '</td><td>' + r.salerate + '</td><td>' + r.amount + '</td></tr>'
            ).join('')
            : filteredDataBill.map((r, i) =>
                '<tr><td>' + (i + 1) + '</td><td>' + formatDate(r.date1) + '</td><td>' + r.billno + '</td><td>' + r.cashamout + '</td><td>' + r.cardamount + '</td><td>' + r.upiamount + '</td></tr>'
            ).join('');

        const headers = selectedOption === 'itemWise'
            ? '<th>Sl.No</th><th>Item Name</th><th>Quantity</th><th>Rate</th><th>Amount</th>'
            : '<th>Sl.No</th><th>Date</th><th>Bill Details</th><th>Cash</th><th>Card</th><th>UPI</th>';

        const totalAmount = selectedOption === 'itemWise'
            ? filteredDataItem.reduce((s, r) => s + parseFloat(r.amount), 0).toFixed(2)
            : filteredDataBill.reduce((s, r) => s + parseFloat(r.cashamout) + parseFloat(r.cardamount) + parseFloat(r.upiamount), 0).toFixed(2);

        const printWindow = window.open('', '_blank');
        const html = '<html><head><title>' + title + '</title></head><body>'
            + '<h3 style="font-family:Times New Roman;font-size:28px;">' + title + '</h3>'
            + '<h4 style="font-family:Times New Roman;font-size:18px;">From: ' + formatDate(fromDate) + ' To: ' + formatDate(toDate) + '</h4>'
            + '<table style="border-collapse:collapse;width:100%;">'
            + '<thead style="background:#f3f3f3;"><tr style="font-size:16px;font-weight:bold;">' + headers + '</tr></thead>'
            + '<tbody style="font-size:14px;">' + rows + '</tbody>'
            + '</table>'
            + '<p style="font-family:Times New Roman;font-size:16px;text-align:right;font-weight:bold;">Total Amount: ' + totalAmount + '</p>'
            + '</body></html>';
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
    };

    const itemTotal = filteredDataItem.reduce((s, r) => s + parseFloat(r.amount), 0).toFixed(2);
    const itemQtyTotal = filteredDataItem.reduce((s, r) => s + parseFloat(r.qty), 0).toFixed(2);
    const billCashTotal = filteredDataBill.reduce((s, r) => s + parseFloat(r.cashamout), 0).toFixed(2);
    const billCardTotal = filteredDataBill.reduce((s, r) => s + parseFloat(r.cardamount), 0).toFixed(2);
    const billUpiTotal = filteredDataBill.reduce((s, r) => s + parseFloat(r.upiamount), 0).toFixed(2);

    return (
        <div className="sale-register-container">
            <div className="sr-header-card">
                <div className="sr-header-content">
                    <div className="sr-row1">
                        <div className="sr-title-section">
                            <span className="sr-title-icon"><AssessmentIcon fontSize="small" /></span>
                            <h1 className="sr-title">Sale Register</h1>
                        </div>
                        <div className="sr-toggle-group">
                            <button
                                className={'sr-toggle-btn' + (selectedOption === 'itemWise' ? ' active' : '')}
                                onClick={() => setSelectedOption('itemWise')}
                            >
                                <span className="sr-toggle-emoji">&#128230;</span>
                                <span className="sr-toggle-caption">Item Wise</span>
                            </button>
                            <button
                                className={'sr-toggle-btn' + (selectedOption === 'billWise' ? ' active' : '')}
                                onClick={() => setSelectedOption('billWise')}
                            >
                                <span className="sr-toggle-emoji">&#128196;</span>
                                <span className="sr-toggle-caption">Bill Wise</span>
                            </button>
                        </div>
                    </div>

                    <div className="sr-divider" />

                    <div className="sr-controls">
                        <div className="sr-date-filters">
                            <div className="sr-date-group">
                                <label className="sr-date-label">From</label>
                                <input
                                    className="sr-date-input"
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="sr-date-group">
                                <label className="sr-date-label">To</label>
                                <input
                                    className="sr-date-input"
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={'sr-search-wrapper' + (searchOpen ? ' open' : '')}>
                            <span className="sr-search-icon" onClick={() => setSearchOpen(function(o) { return !o; })}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input
                                    autoFocus
                                    className="sr-search-input"
                                    type="text"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                                />
                            )}
                        </div>

                        <button className="sr-action-btn sr-btn-refresh" onClick={fetchTableData}>
                            <RefreshIcon fontSize="small" />&nbsp;Refresh
                        </button>
                        <button className="sr-action-btn sr-btn-excel" onClick={handleExportExcel}>
                            <FileDownloadIcon fontSize="small" />&nbsp;Excel
                        </button>                       
                    </div>
                </div>
            </div>

            <div className="sr-table-wrapper">
                <TableContainer component={Paper} className="sr-table-container" elevation={0}>
                    {selectedOption === 'itemWise' && (
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="sr-th">Sl.No</TableCell>
                                    <TableCell className="sr-th">Item Name</TableCell>
                                    <TableCell className="sr-th" align="right">Quantity</TableCell>
                                    <TableCell className="sr-th" align="right">Rate</TableCell>
                                    <TableCell className="sr-th" align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDataItem.map((data, index) => (
                                    <TableRow key={data.id} className="sr-row">
                                        <TableCell className="sr-td">{index + 1}</TableCell>
                                        <TableCell className="sr-td">{data.itemname}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.qty}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.salerate}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.amount}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="sr-total-row">
                                    <TableCell className="sr-td" />
                                    <TableCell className="sr-td">Total</TableCell>
                                    <TableCell className="sr-td" align="right">{itemQtyTotal}</TableCell>
                                    <TableCell className="sr-td" />
                                    <TableCell className="sr-td" align="right">{itemTotal}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    )}
                    {selectedOption === 'billWise' && (
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="sr-th">Sl.No</TableCell>
                                    <TableCell className="sr-th">Date</TableCell>
                                    <TableCell className="sr-th">Bill Details</TableCell>
                                    <TableCell className="sr-th" align="right">Cash Amount</TableCell>
                                    <TableCell className="sr-th" align="right">Card Amount</TableCell>
                                    <TableCell className="sr-th" align="right">UPI Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDataBill.map((data, index) => (
                                    <TableRow key={data.id} className="sr-row">
                                        <TableCell className="sr-td">{index + 1}</TableCell>
                                        <TableCell className="sr-td">{formatDate(data.date1)}</TableCell>
                                        <TableCell className="sr-td">{data.billno}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.cashamout}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.cardamount}</TableCell>
                                        <TableCell className="sr-td" align="right">{data.upiamount}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="sr-total-row">
                                    <TableCell className="sr-td" colSpan={2} />
                                    <TableCell className="sr-td">Total</TableCell>
                                    <TableCell className="sr-td" align="right">{billCashTotal}</TableCell>
                                    <TableCell className="sr-td" align="right">{billCardTotal}</TableCell>
                                    <TableCell className="sr-td" align="right">{billUpiTotal}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </div>
        </div>
    );
}

export default SaleRegister;
